import { Children, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { updatePassword, type UpdatePasswordInput } from 'aws-amplify/auth';
import InnerLayout from '~/components/SignUp/Layout/InnerLayout';
import { PasswordInput } from '~/components/common';
import { useModal } from '~/hooks';
import { ModalParams } from '~/types';
import { passwordsValidAndMatch } from '~/utils';
import useToast from '~/store/useToast';

interface ChangePasswordProps {
  onNextStep: () => void;
}

export default function ChangePassword({ onNextStep }: ChangePasswordProps) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const { showToast } = useToast();
  const { openModal } = useModal();

  const currentPasswordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (error) {
      setOldPassword('');
      if (currentPasswordRef.current) {
        currentPasswordRef.current.focus();
      }
    }
  }, [error]);

  useEffect(() => {
    if (
      oldPassword.trim().length > 0 &&
      passwordsValidAndMatch(newPassword, confirmPassword)
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [newPassword, confirmPassword, setDisabled]);

  const handleOnClick = async () => {
    try {
      const requestData: UpdatePasswordInput = {
        oldPassword,
        newPassword,
      };

      await updatePassword(requestData);

      showToast('success', 'You successfully changed your password');
      onNextStep();
    } catch (error: any) {
      console.error(error);
      if (error.message === 'Incorrect username or password.') {
        setError(true);
      } else if (
        error.message === 'Attempt limit exceeded, please try after some time.'
      ) {
        const modalParams: ModalParams = {
          title: 'Attempt Error',
          content: error.message,
          type: 'single',
          buttonName: 'OK',
        };
        openModal(modalParams);
      }
    }
  };

  const buttonProps = {
    title: 'Change',
    disabled,
    onClick: handleOnClick,
  };

  return (
    <InnerLayout buttonProps={buttonProps}>
      <Container>
        <div>
          <PasswordInput
            placeholder='Current password'
            text={oldPassword}
            setText={setOldPassword}
            inputRef={currentPasswordRef}
          />
          {error && (
            <Error>Current password is incorrect. please try again.</Error>
          )}
        </div>
        <PasswordInput
          placeholder='Password'
          text={newPassword}
          setText={setNewPassword}
          checkingPasswordOpt
        />
        <PasswordInput
          placeholder='Confirm password'
          text={confirmPassword}
          setText={setConfirmPassword}
          checkingPasswordOpt
          confirm
          password={newPassword}
        />
      </Container>
    </InnerLayout>
  );
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 32px;
`;

export const Error = styled.div`
  ${({ theme }) => css`
    display: block;
    margin-top: 3px;
    ${theme.fontStyle.text.caption_medium}
    color: ${theme.themeColor.negative};
  `}
`;
